import { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import DefaultCard from '../../../../components/default/DefaultCard';
import ButtonCreate from '../../../../components/buttons/ButtonCreate';
import LazyLoading from '../../../../components/LazyLoading';
import DefaultModal from '../../../../components/default/DefaultModal';
import { useHistory } from 'react-router-dom';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../../contexts/AppContext';
import { AfpRateDataForm, AfpRateFilters } from '../../../../app/models/AfpRate';
import useAfpRateService from '../../../../app/services/hooks/useAfpRateService';
import { toast } from 'react-toastify';
import AfpRateDataTable from '../tables/AfpRateDataTable';
import AfpRateCreate from '../subpages/AfpRateCreate';
import AfpRateEdit from '../subpages/AfpRateEdit';

interface Props {
    afpId: number;
}

const AfpRateContainer = ({ afpId }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const history = useHistory();
    const { getAllAfpRatesByAfp, fetchingGetAfpRatesByAfp, deleteAfpRate } = useAfpRateService();

    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [afpRateIdEdit, setAfpRateIdEdit] = useState<number>(-1);
    const [afpRates, setAfpRates] = useState<AfpRateDataForm[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [filter, setFilter] = useState<AfpRateFilters>({
        afp_rate_id: null,
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (afpId: number) => {
        setShowingEdit(true);
        setAfpRateIdEdit(afpId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setAfpRateIdEdit(-1);
    };

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const destroy = (afpRateId: number) => {
        const _text = 'Está a punto de eliminar la tarifa de Afp #' + afpRateId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando tarifa de Afp...');
                        deleteAfpRate(afpRateId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La tarifa de Afp no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const reloadTable = () => {
        getAllAfpRatesByAfp(afpId, {
            onSuccess: (response: ServiceResponse) => {
                setAfpRates(response.data.afp_rates);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    return (
        <>
            <DefaultCard>
                {!fetchingGetAfpRatesByAfp ? (
                    <div className="row">
                        <div className="col-12 mb-2">
                            <ButtonCreate title="Nueva tarifa de Afp" callbackCreate={showCreate} />
                        </div>
                        <div className="col-12">
                            {afpRates ? (
                                <AfpRateDataTable
                                    afpRates={afpRates}
                                    edit={showEdit}
                                    destroy={destroy}
                                    loading={fetchingGetAfpRatesByAfp}
                                    totalRows={totalRows}
                                    filter={filter}
                                    setFilter={setFilter}
                                />
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <LazyLoading height="300" />
                )}
            </DefaultCard>
            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Tarifa de Afp"
                    backdrop={true}
                    showFooter={false}
                >
                    <AfpRateCreate
                        afpId={afpId}
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Tarifa de Afp"
                    backdrop={true}
                    showFooter={false}
                >
                    <AfpRateEdit
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                        afpRateId={afpRateIdEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default AfpRateContainer;
